/**
 * Premium /services page — complete service inventory (routes + keyword capabilities).
 */

export const PROOF_METRICS = [
  {
    id: 'categories',
    value: '10+',
    label: 'production project categories',
    detail: 'Web, ecommerce, AI, CMS, ERP, integrations, and support.',
  },
  {
    id: 'fullstack',
    value: '360°',
    label: 'full-stack delivery',
    detail: 'Design, frontend, backend, integrations, and post-launch care.',
  },
  {
    id: 'workflows',
    value: 'Multi-stack',
    label: 'integration-ready workflows',
    detail: 'AI, ecommerce, CMS, CRM, ERP, analytics, and search systems.',
  },
  {
    id: 'support',
    value: 'Long-term',
    label: 'optimization & maintenance',
    detail: 'Performance, security, monitoring, and continuous improvement.',
  },
];

export const DELIVERY_FLOW = [
  {
    id: 'discover',
    step: '01',
    title: 'Discover',
    desc: 'Audit goals, blockers, users, systems, and success metrics.',
  },
  {
    id: 'architect',
    step: '02',
    title: 'Architect',
    desc: 'Define UX, data flow, tech stack, integrations, and delivery roadmap.',
  },
  {
    id: 'build',
    step: '03',
    title: 'Build',
    desc: 'Ship frontend, backend, CMS, ecommerce, and AI workflows with QA.',
  },
  {
    id: 'integrate',
    step: '04',
    title: 'Integrate',
    desc: 'Connect CRM, ERP, payments, analytics, forms, and search platforms.',
  },
  {
    id: 'optimize',
    step: '05',
    title: 'Optimize',
    desc: 'Improve speed, conversion, Core Web Vitals, and operational reliability.',
  },
  {
    id: 'support',
    step: '06',
    title: 'Support',
    desc: 'Monitor, fix, evolve, and maintain production systems after launch.',
  },
];

export const HERO_SYSTEM_NODES = [
  'Strategy',
  'UX',
  'Frontend',
  'Backend',
  'AI',
  'Commerce',
  'Integrations',
  'Support',
];

export type ServicePillar = {
  id: string;
  title: string;
  proof: string;
  iconKey: string;
  links: { to: string; label: string }[];
  services: string[];
};

export const SERVICE_PILLARS: ServicePillar[] = [
  {
    id: 'web-product',
    title: 'Web & Product Engineering',
    proof: 'Senior-led React, Next.js, Python, and Node.js delivery for scalable products.',
    iconKey: 'code',
    links: [
      { to: '/web-development', label: 'Web development' },
      { to: '/web-design', label: 'Web design' },
      { to: '/mobile-app', label: 'Mobile apps' },
    ],
    services: [
      'React frontend development',
      'Next.js web applications',
      'Python development & APIs',
      'Node.js backend engineering',
      'Product engineering strategy',
      'Platform architecture planning',
      'Dynamic websites & portals',
      'MVP development and scaling',
    ],
  },
  {
    id: 'ai',
    title: 'AI Automation & Intelligent Assistants',
    proof: 'Practical AI workflows connected to CRM, support, and operations—not demos.',
    iconKey: 'robot',
    links: [
      { to: '/ai-automation', label: 'AI automation services' },
      { to: '/getintouch', label: 'Plan an AI use case' },
    ],
    services: [
      'AI solutions & automation',
      'AI chatbot development',
      'AI voice / talk assistants',
      'Generative AI integrations',
      'Workflow automation',
      'Prompt engineering & guardrails',
      'Lead qualification bots',
      'Support assistant handoffs',
    ],
  },
  {
    id: 'ecommerce',
    title: 'Ecommerce, Magento & WooCommerce',
    proof: 'Conversion-focused storefronts with checkout, catalog, and operations tooling.',
    iconKey: 'cart',
    links: [
      { to: '/ecommerce-development', label: 'Ecommerce development' },
      { to: '/magento-development', label: 'Magento development' },
      { to: '/woocommerce-payment-crm-integrations', label: 'WooCommerce integrations' },
    ],
    services: [
      'Ecommerce solutions provider',
      'Magento & Adobe Commerce',
      'WooCommerce payment gateway integration',
      'Ecommerce order processing',
      'Checkout & catalog optimization',
      'Marketplace cataloging (Amazon, Flipkart)',
      'Commerce ERP connections',
      'Lead management services',
    ],
  },
  {
    id: 'cms-education',
    title: 'WordPress, CMS, Forms & Education Platforms',
    proof: 'Roots Sage, flexible content, admissions funnels, and education search systems.',
    iconKey: 'wordpress',
    links: [
      { to: '/wordpress-development', label: 'WordPress development' },
      { to: '/education-website-development', label: 'Education websites' },
      { to: '/education-search-platforms', label: 'Education search' },
    ],
    services: [
      'Roots Sage WordPress themes',
      'ACF flexible content sections',
      'Education website development',
      'Course & campus discovery UX',
      'FormAssembly & Marketo integrations',
      'Multi-step forms & CRM sync',
      'Algolia & SiteSearch360 search',
      'Admissions funnel optimization',
    ],
  },
  {
    id: 'integrations',
    title: 'Backend APIs, CRM, ERP & Integrations',
    proof: 'Secure APIs and connected systems your operations team can rely on daily.',
    iconKey: 'plug',
    links: [
      { to: '/form-crm-integrations', label: 'Forms & CRM' },
      { to: '/erp-development', label: 'Commerce ERP' },
      { to: '/web-development', label: 'Backend & APIs' },
    ],
    services: [
      'REST & microservice APIs',
      'Salesforce / CRM integration',
      'Custom ERP development',
      'Webhook & queue processing',
      'Payment & shipping integrations',
      'Data & workflow automation',
      'Event-driven operations',
      'Third-party system connectivity',
    ],
  },
  {
    id: 'performance',
    title: 'Performance, Hosting, Security & Maintenance',
    proof: 'Production readiness: speed, hosting, security, and measurable improvements.',
    iconKey: 'shield',
    links: [
      { to: '/website-maintenance', label: 'Website maintenance' },
      { to: '/website-maintenance', label: 'Web hosting' },
      { to: '/analytics-tag-management', label: 'Analytics & GTM' },
    ],
    services: [
      'Website optimization & Core Web Vitals',
      'Performance engineering',
      'Hosting & infrastructure',
      'Security updates & monitoring',
      'GTM & dataLayer setup',
      'Privacy & consent widgets',
      'Post-launch support',
      'Continuous improvement cycles',
    ],
  },
];

export type DirectoryGroup = {
  id: string;
  title: string;
  items: { label: string; to: string }[];
};

export const COMPLETE_SERVICE_DIRECTORY: DirectoryGroup[] = [
  {
    id: 'strategy',
    title: 'Strategy, UX & Design',
    items: [
      { label: 'Business website design', to: '/web-design' },
      { label: 'Conversion-focused UX', to: '/web-design' },
      { label: 'Graphic design & visuals', to: '/web-design' },
      { label: 'Product engineering strategy', to: '/web-development' },
      { label: 'Platform architecture planning', to: '/web-development' },
      { label: 'Technology consulting', to: '/getintouch' },
    ],
  },
  {
    id: 'frontend',
    title: 'Frontend & Product Engineering',
    items: [
      { label: 'Web development services', to: '/web-development' },
      { label: 'React frontend development', to: '/web-development' },
      { label: 'Next.js web applications', to: '/web-development' },
      { label: 'Dynamic websites & portals', to: '/web-development' },
      { label: 'React Native mobile apps', to: '/mobile-app' },
      { label: 'Component-driven UI systems', to: '/web-development' },
    ],
  },
  {
    id: 'backend',
    title: 'Backend, APIs & Data',
    items: [
      { label: 'Python development', to: '/web-development' },
      { label: 'Node.js backend engineering', to: '/web-development' },
      { label: 'API & microservice development', to: '/web-development' },
      { label: 'Data pipelines & automation', to: '/web-development' },
      { label: 'Webhook & queue workers', to: '/web-development' },
      { label: 'Database optimization', to: '/web-development' },
    ],
  },
  {
    id: 'ai',
    title: 'AI, Chatbots & Voice',
    items: [
      { label: 'AI automation services', to: '/ai-automation' },
      { label: 'AI chatbot development', to: '/ai-automation' },
      { label: 'AI voice assistants', to: '/ai-automation' },
      { label: 'Workflow automation', to: '/ai-automation' },
      { label: 'LLM integrations', to: '/ai-automation' },
      { label: 'AI governance & guardrails', to: '/ai-automation' },
    ],
  },
  {
    id: 'ecommerce',
    title: 'Ecommerce, Magento & WooCommerce',
    items: [
      { label: 'Ecommerce development', to: '/ecommerce-development' },
      { label: 'Magento development', to: '/magento-development' },
      { label: 'WooCommerce CRM & payments', to: '/woocommerce-payment-crm-integrations' },
      { label: 'Ecommerce order processing', to: '/ecommerce-development' },
      { label: 'Marketplace cataloging', to: '/marketplace-cataloging' },
      { label: 'Lead management services', to: '/form-crm-integrations' },
    ],
  },
  {
    id: 'cms',
    title: 'WordPress, CMS & Education',
    items: [
      { label: 'WordPress development', to: '/wordpress-development' },
      { label: 'Roots Sage theme experts', to: '/wordpress-development' },
      { label: 'Education website development', to: '/education-website-development' },
      { label: 'Education search platforms', to: '/education-search-platforms' },
      { label: 'Course discovery UX', to: '/education-website-development' },
      { label: 'Flexible content architecture', to: '/wordpress-development' },
    ],
  },
  {
    id: 'crm-erp',
    title: 'CRM, Forms, ERP & Operations',
    items: [
      { label: 'Form & CRM integrations', to: '/form-crm-integrations' },
      { label: 'Salesforce / CRM integration', to: '/form-crm-integrations' },
      { label: 'FormAssembly & Marketo', to: '/form-crm-integrations' },
      { label: 'Commerce ERP development', to: '/erp-development' },
      { label: 'Order & inventory workflows', to: '/erp-development' },
      { label: 'Operational dashboards', to: '/erp-development' },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics, Search, Privacy & Widgets',
    items: [
      { label: 'Analytics & tag management', to: '/analytics-tag-management' },
      { label: 'GTM & dataLayer setup', to: '/analytics-tag-management' },
      { label: 'Privacy & translation widgets', to: '/privacy-translation-widgets' },
      { label: 'OneTrust & consent', to: '/privacy-translation-widgets' },
      { label: 'Trustpilot & chat widgets', to: '/privacy-translation-widgets' },
      { label: 'Form & campaign tracking QA', to: '/analytics-tag-management' },
    ],
  },
  {
    id: 'hosting',
    title: 'Hosting, Maintenance & Optimization',
    items: [
      { label: 'Website maintenance', to: '/website-maintenance' },
      { label: 'Web hosting services', to: '/website-maintenance' },
      { label: 'Core Web Vitals optimization', to: '/website-maintenance' },
      { label: 'Performance engineering', to: '/website-maintenance' },
      { label: 'Security & production readiness', to: '/website-maintenance' },
      { label: 'Post-launch support', to: '/website-maintenance' },
    ],
  },
  {
    id: 'supporting',
    title: 'Mobile, Graphics & Supporting Services',
    items: [
      { label: 'React Native mobile apps', to: '/mobile-app' },
      { label: 'Graphic design', to: '/web-design' },
      { label: 'Portfolio & case studies', to: '/portfolio' },
      { label: 'Free technical audit', to: '/getintouch' },
      { label: 'Strategy consultation', to: '/getintouch' },
      { label: 'Contact our team', to: '/getintouch' },
    ],
  },
];

export const RELIABILITY_PROOF = [
  {
    id: 'senior',
    title: 'Senior engineering judgment',
    text: 'Architecture, scope, and technical decisions are led by experienced engineers—not template handoffs.',
  },
  {
    id: 'scope',
    title: 'Clear scope & delivery roadmap',
    text: 'We define milestones, integrations, and acceptance criteria before build so delivery stays predictable.',
  },
  {
    id: 'maintainable',
    title: 'Maintainable, reusable code',
    text: 'Component-driven UI, documented APIs, and clean module boundaries that your team can extend.',
  },
  {
    id: 'qa',
    title: 'Integration QA & production readiness',
    text: 'Forms, CRM sync, payments, analytics, and search are tested end-to-end before go-live.',
  },
  {
    id: 'reliability',
    title: 'Post-launch reliability cycles',
    text: 'Monitoring, fixes, performance tuning, and iterative improvements after launch—not just delivery.',
  },
  {
    id: 'communication',
    title: 'Transparent communication',
    text: 'Direct updates, honest trade-offs, and support channels that keep projects moving without surprises.',
  },
];

export const TECHNOLOGY_KEYWORDS = [
  'React',
  'Next.js',
  'Node.js',
  'Python',
  'WordPress',
  'Roots Sage',
  'Magento',
  'WooCommerce',
  'Salesforce',
  'Marketo',
  'FormAssembly',
  'WPForms',
  'CRM',
  'ERP',
  'GTM',
  'dataLayer',
  'Core Web Vitals',
  'Algolia',
  'SiteSearch360',
  'OneTrust',
  'GTranslate',
  'Trustpilot',
  'Google Maps',
  'React Native',
  'Adobe Commerce',
  'API integrations',
  'AI automation',
];
